import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import * as signalR from '@microsoft/signalr'

import { logger } from '@/lib/logger'
import { useAuthStore } from '@/stores/auth'

import { ConnectionTicketError, getConnectionTicket } from './connection-ticket'
import { getReconnectDelay } from './reconnect-delay'
import type {
  ConnectionStatus,
  UserHubContextValue,
  UserHubEventListener,
  UserHubMessage,
  UserHubMessageType,
} from './user-hub.types'

const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL

const UserHubContext = createContext<UserHubContextValue | null>(null)

export function UserHubProvider({ children }: PropsWithChildren) {
  const isAuthenticated = useAuthStore((state) => !!state.accessToken)
  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const listenersRef = useRef<Map<string, Set<UserHubEventListener>>>(new Map())
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')

  const handleMessage = useCallback((message: UserHubMessage) => {
    logger.debug('Received message', { type: message.type, message }, 'signalr')

    const listeners = listenersRef.current.get(message.type)
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(message.payload)
        } catch (error) {
          logger.error(
            'Error in event listener',
            error instanceof Error ? error : undefined,
            { messageType: message.type },
            'signalr',
          )
        }
      })
    }
  }, [])

  const subscribe = useCallback(
    <T = unknown,>(
      eventType: UserHubMessageType,
      listener: UserHubEventListener<T>,
    ): (() => void) => {
      if (!listenersRef.current.has(eventType)) {
        listenersRef.current.set(eventType, new Set())
      }
      listenersRef.current.get(eventType)!.add(listener as UserHubEventListener)

      return () => {
        const listeners = listenersRef.current.get(eventType)
        if (listeners) {
          listeners.delete(listener as UserHubEventListener)
          if (listeners.size === 0) {
            listenersRef.current.delete(eventType)
          }
        }
      }
    },
    [],
  )

  useEffect(() => {
    if (!isAuthenticated || !SIGNALR_URL) {
      return
    }

    let stopped = false
    let refused = false
    const isStopped = () => stopped
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hubs/user`, {
        accessTokenFactory: async () => {
          try {
            const ticket = await getConnectionTicket()
            refused = false
            return ticket
          } catch (error) {
            refused = error instanceof ConnectionTicketError && error.terminal
            throw error
          }
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) =>
          refused ? null : getReconnectDelay(retryContext.previousRetryCount),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connectionRef.current = connection

    const scheduleStart = () => {
      if (isStopped()) return
      const delay = getReconnectDelay(attempt)
      attempt += 1
      retryTimer = setTimeout(() => {
        void start()
      }, delay)
    }

    const start = async () => {
      if (isStopped()) return
      setStatus('connecting')
      try {
        await connection.start()
        if (isStopped()) return
        attempt = 0
        setStatus('connected')
        logger.info('Connected', undefined, 'signalr')
      } catch (error) {
        if (isStopped()) return
        logger.warning(
          'Failed to connect',
          { error: error instanceof Error ? error.message : String(error) },
          'signalr',
        )
        setStatus('error')
        if (!refused) scheduleStart()
      }
    }

    connection.on('ReceiveNotification', handleMessage)

    connection.onreconnecting(() => {
      logger.info('Reconnecting...', undefined, 'signalr')
      setStatus('connecting')
    })

    connection.onreconnected(() => {
      logger.info('Reconnected', undefined, 'signalr')
      setStatus('connected')
    })

    connection.onclose((error) => {
      if (isStopped()) return
      logger.info('Connection closed', { error: error?.message }, 'signalr')
      if (refused) {
        setStatus('error')
        return
      }
      setStatus('disconnected')
      scheduleStart()
    })

    void start()

    return () => {
      stopped = true
      clearTimeout(retryTimer)
      connectionRef.current = null
      void connection.stop()
      setStatus('disconnected')
    }
  }, [isAuthenticated, handleMessage])

  const contextValue: UserHubContextValue = {
    status,
    isConnected: status === 'connected',
    subscribe,
  }

  return (
    <UserHubContext.Provider value={contextValue}>
      {children}
    </UserHubContext.Provider>
  )
}

export function useUserHub(): UserHubContextValue {
  const context = useContext(UserHubContext)
  if (!context) {
    throw new Error('useUserHub must be used within a UserHubProvider')
  }
  return context
}
